import { ContentHubData } from "@app/types/ServerCustomContext";
import { getGeniusLink } from "@app/utils/trackers/skimlinks";
import { configContentHub } from "@pub/config";
import {
  Ads,
  Button,
  ResponsiveImage,
  Typography,
} from "@sphtech/dmg-design-system";
import cx from "classnames";
import { ReactNode } from "react";

import { ResolvedExternalLinkElement } from "../../Article/elements";
import { Annotation } from "../../Article/StorylineElements/Annotation/Annotation";
import { ColumnImage } from "../../Article/StorylineElements/ColumnImage";
import CustomHtml from "../../Article/StorylineElements/CustomHtml";
import StorylineImage from "../../Article/StorylineElements/Image";
import List from "../../Article/StorylineElements/List";
import SubHeadingElement from "../../Article/StorylineElements/SubHeadingElement";
import styles from "../contenthub.module.css";

const CONTENT_HUB_BODY_CTA_ANCHOR = "content-hub-body-cta";

const { Heading, Body, Label, Disclaimer } = Typography;

type ContentHubBodyContentProps = {
  data: {
    name: ContentHubData["name"];
    nameZH?: ContentHubData["nameZH"];
    storyPostData?: ContentHubData["storyPostData"];
  };
  hasOverlapContainer?: boolean;
};

export default function ContentHubBodyContent({
  data,
  hasOverlapContainer,
}: ContentHubBodyContentProps): ReactNode {
  const hasHeading = !!configContentHub.configBodyContent.withHeading;
  const hasBodyElements = !!data.storyPostData?.bodyElements?.length;
  const hasBroughtToYouByContent =
    !!data.storyPostData?.broughtToYouBy?.data.length;
  const hasAd = configContentHub.configBodyContent.hasAd;

  // If no content, return null to prevent rendering empty container
  if (!hasHeading && !hasBodyElements && !hasBroughtToYouByContent) {
    return null;
  }

  return (
    <div
      className={cx(
        styles.contenthubBodyContent,
        hasOverlapContainer && styles.overlapContainer,
        hasAd && styles.withAd,
      )}
    >
      <div className={styles.mainContentWrapper}>
        {hasHeading && (
          <div className={styles.contenthubHeading}>
            <Heading.SectionTitle nameZH={data.nameZH}>
              {data.name}
            </Heading.SectionTitle>
          </div>
        )}

        {hasBodyElements && (
          <div className={styles.contenthubPostBody}>
            {data.storyPostData?.bodyElements?.map((element, index) => {
              if (element.type === "paragraph") {
                // Temporary workaround: detect CTA via htmlAnchor on a paragraph.
                // Switch to a proper `call_to_action` element type check once CUE
                // enables that type for Content Hub.
                if (
                  element.additionalFields.htmlAnchor?.value ===
                  CONTENT_HUB_BODY_CTA_ANCHOR
                ) {
                  const externalLink = element.annotations?.find(
                    (annotation) => annotation.name === "external_link",
                  )?.value as ResolvedExternalLinkElement | undefined;
                  const href = externalLink?.additionalFields.uri?.value;

                  if (href) {
                    return (
                      <Button
                        key={index}
                        variant="secondary"
                        href={getGeniusLink(href)}
                        target={
                          externalLink.additionalFields.newWindow?.value
                            ? "_blank"
                            : "_self"
                        }
                      >
                        {element.value}
                      </Button>
                    );
                  }
                }

                return (
                  <Body key={index}>
                    <Annotation element={element} />
                  </Body>
                );
              }

              if (element.type === "sub_head") {
                return <SubHeadingElement key={index} element={element} />;
              }

              if (element.type === "image") {
                return (
                  <StorylineImage
                    key={index}
                    element={element}
                    bodyMaxWidth={1000}
                  />
                );
              }

              if (element.type === "embed") {
                return <CustomHtml key={index} element={element} />;
              }

              if (
                element.type === "list_bulleted" ||
                element.type === "list_numbered"
              ) {
                return <List key={index} element={element} />;
              }

              if (element.type === "column_images") {
                return (
                  <div key={index} className={styles.contenthubColumnImages}>
                    <ColumnImage
                      key={index}
                      element={element}
                      bodyMaxWidth={1000}
                      imageCrop={
                        configContentHub.layout?.columnImages?.imageCrop
                      }
                    />
                  </div>
                );
              }
            })}
          </div>
        )}

        {/* Brought to you by will always at the bottom */}
        {hasBroughtToYouByContent && (
          <>
            {data.storyPostData?.broughtToYouBy?.headline && (
              <div className={styles.broughtToYouByContainer}>
                <Disclaimer>
                  {data.storyPostData.broughtToYouBy.headline}
                </Disclaimer>
              </div>
            )}

            <div className={styles.contenthubPostBodyColumnImageContainer}>
              {data.storyPostData?.broughtToYouBy?.data.map(
                ({ title, targetUrl, image }, index) => {
                  return (
                    <div
                      key={index}
                      className={styles.contenthubPostBodyColumnImage}
                    >
                      <a href={targetUrl}>
                        <ResponsiveImage
                          alt={image?.alt || ""}
                          src={image?.src || ""}
                          srcHeight={image?.srcHeight || 0}
                          srcWidth={image?.srcWidth || 0}
                          displayWidth={320}
                        />
                      </a>

                      <div className={styles.columnImagInfo}>
                        <Label key={index}>{title}</Label>
                      </div>
                    </div>
                  );
                },
              )}
            </div>
          </>
        )}
      </div>
      {hasAd && <Ads.AdsSideBar />}
    </div>
  );
}
